<template>
  <div v-if="termin" class="termin-detail-container pa-0">
    <div
      class="termin-hero position-relative d-flex align-end pb-8 px-4 px-md-8 justify-center"
      :style="{
        backgroundImage: `url(${
          termin.thumbnailUrl ||
          'data:image/svg+xml;utf8,<svg xmlns=\\\'http://www.w3.org/2000/svg\\\' width=\\\'1200\\\' height=\\\'400\\\'><rect width=\\\'1200\\\' height=\\\'400\\\' fill=\\\'%231e1e1e\\\'/><text x=\\\'50%\\\' y=\\\'50%\\\' font-family=\\\'sans-serif\\\' font-size=\\\'36\\\' fill=\\\'%23d4af37\\\' text-anchor=\\\'middle\\\' dominant-baseline=\\\'middle\\\'>Akademie Schulung</text></svg>'
        })`,
      }">
      <div class="hero-overlay"></div>
      <div class="position-relative z-1 w-100" style="max-width: 1200px; margin: 0 auto">
        <v-btn icon dark class="mb-4" @click="$router.push('/akademie/schulungen')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <div class="d-flex align-center flex-wrap mb-3">
          <v-chip class="gold-border mr-2 mb-1" color="black" text-color="#D4AF37" small outlined>
            {{ getPhase(termin) }}
          </v-chip>
          <v-chip v-if="termin.isRefresher" class="mb-1" color="grey darken-3" text-color="white" small> Auffrischungskurs </v-chip>
        </div>

        <h1 class="text-h4 text-md-h3 font-weight-bold white--text mb-2">{{ termin.title }}</h1>
        <p class="text-subtitle-2 text-md-subtitle-1 grey--text text--lighten-2 max-w-600">{{ termin.description }}</p>
      </div>
    </div>

    <v-container fluid class="content-container pa-4 pa-md-8" style="max-width: 1200px">
      <v-row>
        <v-col cols="12" md="8">
          <!-- Info Cards -->
          <v-row>
            <v-col cols="12" sm="6">
              <v-card class="info-card rounded-xl pa-4 d-flex align-center h-100" dark elevation="0">
                <v-icon color="#D4AF37" size="40" class="mr-4">mdi-calendar-clock</v-icon>
                <div>
                  <div class="text-caption grey--text text-uppercase">Wann</div>
                  <div class="font-weight-bold">{{ formatDate(termin.startsAt) }}</div>
                  <div class="text-caption grey--text">{{ formatTime(termin.startsAt) }} - {{ formatTime(termin.endsAt) }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6">
              <v-card class="info-card rounded-xl pa-4 d-flex align-center h-100" dark elevation="0">
                <v-icon color="#D4AF37" size="40" class="mr-4">mdi-map-marker</v-icon>
                <div>
                  <div class="text-caption grey--text text-uppercase">Wo</div>
                  <div class="font-weight-bold">{{ termin.location }}</div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <h3 class="text-h6 font-weight-bold white--text mt-8 mb-4">Voraussetzungen</h3>
          <v-alert v-if="missingModules.length > 0" type="error" outlined text class="rounded-lg mb-4">
            <div class="font-weight-bold mb-1">Fehlende Module:</div>
            <ul class="pl-4 text-caption">
              <li v-for="mod in missingModules" :key="mod._id">{{ mod.title }}</li>
            </ul>
          </v-alert>
          <v-alert v-else type="success" outlined text class="rounded-lg mb-4"> Du erfüllst alle Voraussetzungen für diesen Termin. </v-alert>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="booking-card rounded-xl pa-6 sticky-top" dark elevation="12">
            <h3 class="text-h5 font-weight-bold mb-4">Anmeldung</h3>

            <div class="d-flex justify-space-between align-center mb-6 pb-6 border-bottom">
              <div>
                <div class="text-caption grey--text">Verfügbare Plätze</div>
                <div class="text-h4 font-weight-bold gold-text">
                  {{ Math.max(0, termin.maxParticipants - (termin.registeredCount || 0)) }}
                  <span class="text-h6 grey--text">/ {{ termin.maxParticipants }}</span>
                </div>
              </div>
              <v-progress-circular
                :value="Math.min(100, ((termin.registeredCount || 0) / termin.maxParticipants) * 100)"
                color="#D4AF37"
                size="60"
                width="6"></v-progress-circular>
            </div>

            <!-- Schon angemeldet: Status + Cancel -->
            <template v-if="termin.myRegistration && termin.myRegistration.status !== 'cancelled'">
              <v-alert
                :type="termin.myRegistration.status === 'waiting_list' ? 'warning' : 'success'"
                outlined
                text
                dense
                class="rounded-lg mb-4">
                <div class="font-weight-bold">
                  {{ statusLabel(termin.myRegistration.status) }}
                </div>
                <div class="text-caption">Du bist für diesen Termin eingetragen.</div>
              </v-alert>
              <v-btn block large rounded color="error" outlined :loading="cancelling" @click="cancel">
                <v-icon left>mdi-close-circle-outline</v-icon> Anmeldung stornieren
              </v-btn>
            </template>

            <template v-else>
              <v-tooltip bottom :disabled="canRegister">
                <template v-slot:activator="{ on, attrs }">
                  <div v-on="on" v-bind="attrs">
                    <v-btn
                      block
                      x-large
                      rounded
                      :color="canRegister ? '#D4AF37' : 'grey darken-3'"
                      :class="{ 'black--text font-weight-bold': canRegister, 'grey--text': !canRegister }"
                      :disabled="!canRegister || registering"
                      :loading="registering"
                      @click="register">
                      <v-icon left v-if="canRegister">mdi-check-circle-outline</v-icon>
                      <span v-if="(termin.registeredCount || 0) >= termin.maxParticipants && canRegister">Auf Warteliste setzen</span>
                      <span v-else>Jetzt anmelden</span>
                    </v-btn>
                  </div>
                </template>
                <span>Du musst zuerst die fehlenden Module abschliessen.</span>
              </v-tooltip>
            </template>

            <p class="text-caption grey--text text-center mt-4">
              Kostenfreie Stornierung bis {{ termin.cancellationDeadlineHours }}h vor Beginn möglich.
            </p>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right elevation="24">
      <div class="d-flex align-center">
        <v-icon color="white" class="mr-2">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar.show = false"><v-icon>mdi-close</v-icon></v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "SchulungDetail",
  data() {
    return {
      termin: null,
      missingModules: [],
      registering: false,
      cancelling: false,
      loading: false,
      snackbar: { show: false, color: "success", text: "", icon: "mdi-check-circle" },
    }
  },
  computed: {
    canRegister() {
      return this.missingModules.length === 0
    },
  },
  mounted() {
    this.loadTermin()
  },
  methods: {
    async loadTermin() {
      this.loading = true
      try {
        const res = await api.akademie.fetchTermin(this.$route.params.id)
        this.termin = res
        if (this.termin && this.termin.requiredModules) {
          const progresses = await api.akademie.fetchProgress()
          this.missingModules = this.termin.requiredModules.filter((rm) => {
            const prog = progresses.find((p) => (p.module && (p.module._id || p.module)) === rm._id)
            return !(prog && prog.completedAt)
          })
        }
      } catch (err) {
        console.error("Fehler beim Laden des Termins:", err)
        this.notify("error", "Termin konnte nicht geladen werden", "mdi-alert-circle")
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      if (!date) return ""
      return new Date(date).toLocaleDateString("de-CH", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    },
    formatTime(date) {
      if (!date) return ""
      return new Date(date).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }) + " Uhr"
    },
    getPhase(termin) {
      if (!termin.registrationStart || !termin.registrationEnd) return "Unbekannt"
      const now = new Date()
      const start = new Date(termin.registrationStart)
      const end = new Date(termin.registrationEnd)
      if (now < start) return "Geplant"
      if (now > end) return "Geschlossen"
      return "Geöffnet"
    },
    statusLabel(s) {
      const map = { registered: "Angemeldet", waiting_list: "Auf Warteliste", attended: "Teilgenommen" }
      return map[s] || s
    },
    notify(color, text, icon = "mdi-information") {
      this.snackbar = { show: true, color, text, icon }
    },
    async register() {
      if (!this.canRegister || !this.termin) return
      // Schon angemeldet? -> nicht erneut posten
      if (this.termin.myRegistration && this.termin.myRegistration.status !== "cancelled") {
        this.notify("warning", "Du bist bereits angemeldet.", "mdi-information")
        return
      }
      this.registering = true
      try {
        const reg = await api.akademie.registerForTermin(this.termin._id)
        // Vue 2 reaktiv: $set verwenden, da myRegistration vorher null war
        this.$set(this.termin, "myRegistration", { _id: reg._id, status: reg.status })
        this.$set(this.termin, "registeredCount", (this.termin.registeredCount || 0) + (reg.status === "registered" ? 1 : 0))
        if (reg.status === "waiting_list") {
          this.notify("warning", "Plätze ausgebucht – du stehst auf der Warteliste.", "mdi-clock-outline")
        } else {
          this.notify("success", "Anmeldung erfolgreich!", "mdi-check-circle")
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error) || "Anmeldung fehlgeschlagen"
        // Falls Backend sagt "bereits angemeldet" mit Registration -> Status übernehmen
        if (err.response && err.response.status === 409 && err.response.data && err.response.data.registration) {
          this.$set(this.termin, "myRegistration", {
            _id: err.response.data.registration._id,
            status: err.response.data.registration.status,
          })
        }
        this.notify("error", msg, "mdi-alert-circle")
      } finally {
        this.registering = false
      }
    },
    async cancel() {
      if (!this.termin || !this.termin.myRegistration) return
      if (!confirm("Anmeldung wirklich stornieren?")) return
      this.cancelling = true
      try {
        await api.akademie.cancelRegistration(this.termin.myRegistration._id)
        this.$set(this.termin, "myRegistration", null)
        if (this.termin.registeredCount > 0) this.termin.registeredCount -= 1
        this.notify("success", "Anmeldung storniert.", "mdi-check-circle")
      } catch (err) {
        this.notify("error", "Stornierung fehlgeschlagen.", "mdi-alert-circle")
      } finally {
        this.cancelling = false
      }
    },
  },
}
</script>

<style scoped>
.termin-detail-container {
  min-height: 100vh;
  background-color: #121212;
}

.content-container {
  margin: 0 auto;
}

.termin-hero {
  height: 40vh;
  min-height: 320px;
  background-size: cover;
  background-position: center;
}

@media (min-width: 960px) {
  .termin-hero {
    min-height: 400px;
  }
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, #121212 0%, rgba(18, 18, 18, 0.4) 100%);
  z-index: 0;
}

.z-1 { z-index: 1; }
.w-100 { width: 100%; }
.max-w-600 { max-width: 600px; }
.h-100 { height: 100%; }
.gold-text { color: #d4af37 !important; }
.gold-border { border-color: #d4af37 !important; }

.info-card {
  background: rgba(30, 30, 30, 0.6) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.booking-card {
  background: rgba(20, 20, 20, 0.9) !important;
  border: 1px solid rgba(212, 175, 55, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.8), 0 0 20px rgba(212, 175, 55, 0.05) !important;
}

.sticky-top {
  position: sticky;
  top: 24px;
}

.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
</style>
